import axios from 'axios';
import store from '@Store';
import { AUTHENTICATION, SEAL_NETWORK, NOTIFICATION_HANDLER } from '@Constants/types';
import { STORAGE_TYPE, getFromStorage } from './storageService';
import { LOG_TYPE, addLog } from './loggingService';
import { getErrorMessageByVersion } from './versionService';
import { logout } from '@Actions/authentication';

const UNAUTHORIZED = 401;
const SUCCESS = 200;

const successResponse = response => {
    const { status, data, config } = response;
    const { url } = config;
    /**
     * This endpoint returns both a 200-OK and empty data object, which we are using to determine if the user account is locked or disabled.
        Therefore we have to skip the checking for this one endpoint
     */
    const isTokenUrl =
        url &&
        url.toString().length > 0 &&
        url.toString().startsWith('/seal-ws/security/token?nonce=');

    if (status === SUCCESS && !data && !isTokenUrl) {
        store.dispatch(logout(true));
        store.dispatch({
            type: NOTIFICATION_HANDLER.WARNING,
            payload: 'User account has been locked or not enabled.'
        });
    }
    return response;
};

const errorResponse = error => {
    if (error.response) {
        const { status } = error.response;
        if (status === UNAUTHORIZED) store.dispatch(logout(true));
    }

    return Promise.reject(error);
};

class API_SERVICE {
    constructor() {
        this.setAPI();
        store.subscribe(() => {
            // subscribed
        });
    }

    setAPI() {
        this.SEAL_URL = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
        this.DIGAPP_URL = `${this.SEAL_URL}/seal-digapp`;
        this.authToken = getFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
        this.authType = getFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);

        const headers = { 'Content-Type': 'application/json' };
        if (this.authType === 'basic' || this.authType === 'SAML') {
            headers['X-Session-Token'] = this.authToken;
        } else {
            headers.Authorization = this.authToken;
        }

        this.SEAL_API = axios.create({ baseURL: this.SEAL_URL, headers });
        this.DIGAPP_API = axios.create({ baseURL: this.DIGAPP_URL, headers });

        this.SEAL_API.interceptors.response.use(successResponse, errorResponse);
        this.DIGAPP_API.interceptors.response.use(successResponse, errorResponse);
    }

    refresh() {
        this.refreshBaseUrl();
        this.refreshToken();
    }

    refreshBaseUrl() {
        // Refreshing Seal url
        this.SEAL_URL = getFromStorage('seal_url', STORAGE_TYPE.LOCAL_STORAGE);
        this.SEAL_API.defaults.baseURL = this.SEAL_URL;
        // Refreshing DigApp url
        this.DIGAPP_URL = `${this.SEAL_URL}/seal-digapp`;
        this.DIGAPP_API.defaults.baseURL = this.DIGAPP_URL;
    }

    refreshToken() {
        this.authToken = getFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
        this.authType = getFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
        if (this.authType === 'basic' || this.authType === 'SAML') {
            this.SEAL_API.defaults.headers['X-Session-Token'] = this.authToken;
            this.DIGAPP_API.defaults.headers['X-Session-Token'] = this.authToken;
            delete this.SEAL_API.defaults.headers.Authorization;
            delete this.DIGAPP_API.defaults.headers.Authorization;
        } else {
            this.SEAL_API.defaults.headers.Authorization = this.authToken;
            this.DIGAPP_API.defaults.headers.Authorization = this.authToken;
            delete this.SEAL_API.defaults.headers['X-Session-Token'];
            delete this.DIGAPP_API.defaults.headers['X-Session-Token'];
        }
    }

    setContentType(ContentType) {
        this.SEAL_API.defaults.headers['Content-Type'] = ContentType;
        this.DIGAPP_API.defaults.headers['Content-Type'] = ContentType;
    }

    setAccept(value) {
        this.SEAL_API.defaults.headers.Accept = value;
        this.DIGAPP_API.defaults.headers.Accept = value;
    }

    getVersion = () => {
        const rootReducer = store.getState();
        const { auth } = rootReducer;
        let { version } = auth;

        if (!version || typeof version === 'undefined') {
            version = '99.99.99';
        }

        return version;
    };

    // eslint-disable-next-line class-methods-use-this
    handleError(err, method) {
        try {
            const version = this.getVersion();

            addLog(LOG_TYPE.ERROR, method, err);
            if (err.response) {
                const unhandledMesg = `Unhandled error response:${err.response.data.status} ${
                    err.response.data.message
                }'`;

                if (err.response.data && err.response.data.status === 401) {
                    switch (err.response.data.message) {
                        // Locked Account or Not Enabled
                        case getErrorMessageByVersion('LOCKED_ACCOUNT', version):
                            store.dispatch({
                                type: AUTHENTICATION.LOGIN_FAILURE,
                                payload:
                                    'You cannot login. Either your account is locked or not enabled.'
                            });
                            break;
                        // Invalid X-Session-Token
                        // message is set in authenticationReducer
                        case getErrorMessageByVersion('UNAUTHORIZED', version):
                            store.dispatch({
                                type: SEAL_NETWORK.UNAUTHORIZED
                            });
                            break;
                        // Invalid Credentials
                        case getErrorMessageByVersion('INVALID_CREDENTIALS', version):
                            store.dispatch({
                                type: AUTHENTICATION.LOGIN_FAILURE,
                                payload: 'Please double-check that the information is correct'
                            });
                            break;
                        // Any unhandled 401 message
                        default:
                            addLog(
                                LOG_TYPE.ERROR,
                                'authentication.loginSuccessHandler',
                                unhandledMesg
                            );

                            store.dispatch({
                                type: AUTHENTICATION.LOGIN_FAILURE,
                                payload: err.response.data.message
                            });
                            break;
                    }

                    return;
                }

                if (err.response.data && err.response.data.status !== 401) {
                    addLog(LOG_TYPE.ERROR, 'authentication.loginSuccessHandler', unhandledMesg);

                    store.dispatch({
                        type: AUTHENTICATION.LOGIN_FAILURE,
                        payload:
                            'We could not sign you in to Seal. If something does not seem to work as expected please contact your system administrator..'
                    });

                    return;
                }

                // catch all
                const errMessage =
                    'Catch-all for the apiSevice error handler.  If we get here something has gone badly wrong';
                addLog(LOG_TYPE.ERROR, 'apiService.handleError', errMessage);

                // we must dispatch something as we can't leave the user hanging
                store.dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload: errMessage
                });
            } else {
                if (err && err.message) {
                    store.dispatch({
                        type: SEAL_NETWORK.ERROR_MESSAGE,
                        payload: err.message
                    });

                    return;
                }

                addLog(LOG_TYPE.ERROR, 'apiService.handleError', 'No error information available');

                // App fails to reach seal server
                // message is set in authenticationReducer
                store.dispatch({
                    type: SEAL_NETWORK.SERVER_DOWN_ERROR
                });
            }
        } catch (exception) {
            addLog(LOG_TYPE.ERROR, method, exception);

            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: exception
            });
        }
    }
}

export default new API_SERVICE();
